import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus, Shield, Upload, RefreshCcw, BarChart3, Film, Tv, Users, Clock, Star, X, Radio, Award, User2, Check, Ban, Search as SearchIcon, KeyRound, Mail, Crown, Clapperboard, Bell, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/* ---------------- Users tab (search + admin password reset + create user) ---------------- */
export default function UsersAdmin() {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ total: 0, users: [] });
  const [loading, setLoading] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // user object
  const [resetPw, setResetPw] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetMode, setResetMode] = useState("set"); // "set" | "link"
  const [resetLink, setResetLink] = useState(null);  // { reset_url, expires_at, expires_in_minutes }

  // Create-user modal state
  const [showCreate, setShowCreate] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const emptyCreate = {
    email: "",
    name: "",
    password: "",
    is_admin: false,
    is_filmmaker: false,
    is_content_owner: false,
    is_channel_owner: false,
    subscription_tier: "",
    subscription_status: "inactive",
  };
  const [createForm, setCreateForm] = useState(emptyCreate);

  // Confirm-delete modal state
  const [confirmDelete, setConfirmDelete] = useState(null); // user object or null
  const [confirmBusy, setConfirmBusy] = useState(false);

  const load = async (query = q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { q: query, limit: 50 } });
      setData(data);
    } catch (e) {
      console.error("[admin/users] load failed", e?.response?.status);
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(""); /* initial */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submitSearch = (e) => { e.preventDefault(); load(q); };

  const openReset = (u) => {
    setResetTarget(u);
    setResetMode("set");
    setResetLink(null);
    setResetPw(`Temp-${u.name?.split(" ")[0]?.replace(/[^a-zA-Z]/g, "") || "User"}-${Math.floor(Math.random() * 9000 + 1000)}!`);
  };
  const closeReset = () => { setResetTarget(null); setResetPw(""); setResetLink(null); };

  const deleteUser = (u) => {
    if (u.is_admin) { toast.error("Admin accounts cannot be deleted from the UI"); return; }
    setConfirmDelete(u);
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    setConfirmBusy(true);
    try {
      await api.delete(`/admin/users/${confirmDelete.user_id}`);
      toast.success(`Deleted ${confirmDelete.email}`);
      setConfirmDelete(null);
      load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete user");
    } finally {
      setConfirmBusy(false);
    }
  };

  const submitReset = async () => {
    if (!resetTarget) return;
    if (resetPw.length < 8) { toast.error("Password must be ≥ 8 characters"); return; }
    setResetBusy(true);
    try {
      await api.post(`/admin/users/${resetTarget.user_id}/reset-password`, { new_password: resetPw });
      toast.success(`Password reset for ${resetTarget.email}. Share the new password securely.`);
      closeReset();
      load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetBusy(false);
    }
  };

  const generateLink = async () => {
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${resetTarget.user_id}/reset-link`);
      setResetLink(data);
      toast.success(`One-time link generated — valid for ${data.expires_in_minutes} minutes`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate link");
    } finally {
      setResetBusy(false);
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Could not copy — please select manually");
    }
  };
  const copyPw = () => copyText(resetPw, "Password");
  const copyLink = () => copyText(resetLink?.reset_url || "", "Reset link");

  const genTempPassword = () => {
    const base = (createForm.name?.split(" ")[0] || "User").replace(/[^a-zA-Z]/g, "") || "User";
    return `Temp-${base}-${Math.floor(Math.random() * 9000 + 1000)}!`;
  };

  const openCreate = () => {
    setCreateForm({ ...emptyCreate, password: `Temp-User-${Math.floor(Math.random() * 9000 + 1000)}!` });
    setShowCreate(true);
  };
  const closeCreate = () => { setShowCreate(false); setCreateForm(emptyCreate); };

  const submitCreate = async (e) => {
    e?.preventDefault?.();
    const f = createForm;
    if (!f.email.trim() || !f.name.trim()) { toast.error("Email and name are required"); return; }
    if (f.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setCreateBusy(true);
    try {
      const payload = {
        email: f.email.trim().toLowerCase(),
        name: f.name.trim(),
        password: f.password,
        is_admin: f.is_admin,
        is_filmmaker: f.is_filmmaker,
        is_content_owner: f.is_content_owner,
        is_channel_owner: f.is_channel_owner,
      };
      if (f.subscription_tier) payload.subscription_tier = f.subscription_tier;
      if (f.subscription_status) payload.subscription_status = f.subscription_status;
      const { data: resp } = await api.post("/admin/users", payload);
      toast.success(`User ${resp.user.email} created. Share the password securely.`);
      closeCreate();
      load(q);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed to create user");
    } finally {
      setCreateBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold mb-1">Users</h2>
          <p className="text-sm text-gray-400">Search any user and reset their password if they&apos;re locked out.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-gray-500">
            <span data-testid="users-total">{data.total.toLocaleString()}</span> total accounts
          </div>
          <button
            onClick={openCreate}
            data-testid="users-new-btn"
            className="btn-red px-4 py-2 rounded text-sm font-semibold inline-flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> New user
          </button>
        </div>
      </div>

      <form onSubmit={submitSearch} className="flex items-center gap-3">
        <div className="flex-1 relative max-w-xl">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            data-testid="users-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email, name, or user_id…"
            className="w-full bg-[#141414] border border-white/10 rounded pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#E50914] transition-colors"
          />
        </div>
        <button
          type="submit"
          data-testid="users-search-submit"
          className="btn-red px-5 py-2 rounded text-sm font-semibold disabled:opacity-50"
          disabled={loading}
        >
          {loading ? "Searching…" : "Search"}
        </button>
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); load(""); }}
            className="px-3 py-2 rounded text-xs text-gray-400 hover:text-white border border-white/10 hover:border-white/30 transition-colors"
            data-testid="users-search-clear"
          >
            Clear
          </button>
        )}
      </form>

      <div className="overflow-x-auto border border-white/10 rounded">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] border-b border-white/10 text-xs uppercase tracking-widest text-gray-400">
            <tr>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Subscription</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Roles</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Joined</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.users.length === 0 && !loading && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                {q ? `No users match "${q}"` : "No users."}
              </td></tr>
            )}
            {data.users.map((u) => (
              <tr key={u.user_id} className="border-b border-white/[0.06] hover:bg-white/[0.02]" data-testid={`user-row-${u.user_id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover bg-white/5" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E50914] to-amber-600 flex items-center justify-center text-xs font-bold uppercase">
                        {(u.name || u.email || "?").charAt(0)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-medium truncate flex items-center gap-1.5">
                        {u.name || "(no name)"}
                        {u.is_admin && <Crown className="w-3.5 h-3.5 text-amber-400" title="Admin" />}
                      </div>
                      <div className="text-xs text-gray-500 truncate flex items-center gap-1">
                        <Mail className="w-3 h-3" />{u.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  {u.subscription_status === "active" ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/30 capitalize">
                      {u.subscription_tier || "active"}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-gray-500 border border-white/10">
                      inactive
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell text-xs text-gray-400">
                  <div className="flex flex-wrap gap-1">
                    {u.is_filmmaker && <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">Filmmaker</span>}
                    {u.is_content_owner && <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-300 border border-blue-500/20">Content Owner</span>}
                    {u.is_channel_owner && <span className="px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-300 border border-violet-500/20">Channel Owner</span>}
                    {u.password_reset_by_admin && (
                      <span className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-300 border border-red-500/20" title="Last password set by an admin">Admin reset</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-xs text-gray-500">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-2 justify-end">
                    <button
                      onClick={() => openReset(u)}
                      data-testid={`reset-pw-btn-${u.user_id}`}
                      className="text-xs px-3 py-1.5 rounded border border-white/15 hover:border-[#E50914] hover:bg-[#E50914]/10 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={u.is_admin}
                      title={u.is_admin ? "Cannot reset another admin's password from the UI — use env rotation instead" : "Set a temporary password"}
                    >
                      <KeyRound className="w-3.5 h-3.5" /> Reset password
                    </button>
                    <button
                      onClick={() => deleteUser(u)}
                      data-testid={`delete-user-btn-${u.user_id}`}
                      className="text-xs p-1.5 rounded border border-white/10 text-gray-400 hover:text-red-300 hover:border-red-500/40 hover:bg-red-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      disabled={u.is_admin}
                      title={u.is_admin ? "Cannot delete admin accounts from the UI" : "Delete user"}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Reset password modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" data-testid="reset-pw-modal">
          <div className="bg-[#141414] border border-white/10 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#E50914]" />Account recovery</h3>
                <p className="text-xs text-gray-500 mt-1">{resetTarget.email}</p>
              </div>
              <button onClick={closeReset} className="text-gray-400 hover:text-white p-1" data-testid="reset-pw-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mode toggle */}
            <div className="flex gap-1 mb-5 bg-black/40 rounded p-1 border border-white/5">
              <button
                onClick={() => { setResetMode("set"); setResetLink(null); }}
                data-testid="reset-mode-set"
                className={`flex-1 px-3 py-1.5 rounded text-xs font-semibold transition-colors ${resetMode === "set" ? "bg-[#E50914] text-white" : "text-gray-400 hover:text-white"}`}
              >
                Set temporary password
              </button>
              <button
                onClick={() => { setResetMode("link"); }}
                data-testid="reset-mode-link"
                className={`flex-1 px-3 py-1.5 rounded text-xs font-semibold transition-colors ${resetMode === "link" ? "bg-[#E50914] text-white" : "text-gray-400 hover:text-white"}`}
              >
                Generate reset link
              </button>
            </div>

            {resetMode === "set" ? (
              <>
                <p className="text-sm text-gray-400 mb-4 leading-relaxed">
                  Set a temporary password. User can sign in immediately — share via a secure channel and ask them to change it from Account settings.
                </p>
                <label className="block text-xs uppercase tracking-widest text-gray-400 mb-2">Temporary password (≥ 8 chars)</label>
                <div className="relative">
                  <input
                    data-testid="reset-pw-input"
                    type="text"
                    value={resetPw}
                    onChange={(e) => setResetPw(e.target.value)}
                    className="w-full bg-black/50 border border-white/10 rounded px-3 py-2 pr-20 font-mono text-sm focus:outline-none focus:border-[#E50914]"
                    autoFocus
                  />
                  <button
                    onClick={copyPw}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-white/10"
                    data-testid="reset-pw-copy"
                    type="button"
                  >
                    Copy
                  </button>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <button onClick={closeReset} className="px-4 py-2 text-sm text-gray-400 hover:text-white" data-testid="reset-pw-cancel">
                    Cancel
                  </button>
                  <button
                    onClick={submitReset}
                    disabled={resetBusy || resetPw.length < 8}
                    className="btn-red px-5 py-2 rounded text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
                    data-testid="reset-pw-submit"
                  >
                    {resetBusy && <RefreshCcw className="w-4 h-4 animate-spin" />}
                    Reset & confirm
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-400 mb-4 leading-relaxed">
                  Generate a one-time reset URL the user can click to set their own password. Works even when transactional email delivery is offline.
                  Link is single-use and expires in 15 minutes.
                </p>
                {!resetLink ? (
                  <div className="flex justify-end gap-2 mt-4">
                    <button onClick={closeReset} className="px-4 py-2 text-sm text-gray-400 hover:text-white" data-testid="reset-link-cancel">
                      Cancel
                    </button>
                    <button
                      onClick={generateLink}
                      disabled={resetBusy}
                      className="btn-red px-5 py-2 rounded text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
                      data-testid="reset-link-generate"
                    >
                      {resetBusy && <RefreshCcw className="w-4 h-4 animate-spin" />}
                      Generate link
                    </button>
                  </div>
                ) : (
                  <>
                    <label className="block text-xs uppercase tracking-widest text-amber-300 mb-2">Share this link with the user (expires in {resetLink.expires_in_minutes} min)</label>
                    <div className="relative">
                      <input
                        data-testid="reset-link-url"
                        readOnly
                        value={resetLink.reset_url}
                        className="w-full bg-black/50 border border-amber-500/30 rounded px-3 py-2 pr-20 font-mono text-[11px] focus:outline-none"
                        onFocus={(e) => e.target.select()}
                      />
                      <button
                        onClick={copyLink}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-amber-300 hover:text-white px-2 py-1 rounded hover:bg-white/10"
                        data-testid="reset-link-copy"
                        type="button"
                      >
                        Copy
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">
                      💡 Tip: Send via WhatsApp / Signal / SMS — anything works. The user clicks once and sets their own password. No email infrastructure required.
                    </p>
                    <div className="flex justify-end gap-2 mt-5">
                      <button onClick={closeReset} className="btn-red px-5 py-2 rounded text-sm font-semibold" data-testid="reset-link-done">
                        Done
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Create user modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" data-testid="create-user-modal">
          <form onSubmit={submitCreate} className="bg-[#141414] border border-white/10 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2"><Plus className="w-5 h-5 text-[#E50914]" />Create new user</h3>
                <p className="text-xs text-gray-500 mt-1">Manually onboard a user — email + password required.</p>
              </div>
              <button type="button" onClick={closeCreate} className="text-gray-400 hover:text-white p-1" data-testid="create-user-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-gray-400 mb-1.5">Email</label>
                <input
                  data-testid="create-user-email"
                  type="email"
                  required
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#E50914]"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-gray-400 mb-1.5">Full name</label>
                <input
                  data-testid="create-user-name"
                  type="text"
                  required
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#E50914]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-gray-400 mb-1.5">Password (≥ 8 chars)</label>
                <div className="relative">
                  <input
                    data-testid="create-user-password"
                    type="text"
                    required
                    minLength={8}
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    className="w-full bg-black/50 border border-white/10 rounded px-3 py-2 pr-24 font-mono text-sm focus:outline-none focus:border-[#E50914]"
                  />
                  <button
                    type="button"
                    onClick={() => setCreateForm({ ...createForm, password: genTempPassword() })}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-white/10"
                    data-testid="create-user-gen-password"
                  >
                    Generate
                  </button>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-gray-400 mb-2">Roles</div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: "is_admin", label: "Admin", testid: "create-role-admin" },
                    { key: "is_filmmaker", label: "Filmmaker", testid: "create-role-filmmaker" },
                    { key: "is_content_owner", label: "Content Owner", testid: "create-role-content-owner" },
                    { key: "is_channel_owner", label: "Channel Owner", testid: "create-role-channel-owner" },
                  ].map((r) => (
                    <label key={r.key} className="flex items-center gap-2 text-sm px-3 py-2 rounded border border-white/10 hover:border-white/25 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={r.testid}
                        checked={createForm[r.key]}
                        onChange={(e) => setCreateForm({ ...createForm, [r.key]: e.target.checked })}
                        className="accent-[#E50914]"
                      />
                      {r.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-gray-400 mb-1.5">Subscription tier</label>
                  <select
                    data-testid="create-user-tier"
                    value={createForm.subscription_tier}
                    onChange={(e) => setCreateForm({ ...createForm, subscription_tier: e.target.value, subscription_status: e.target.value ? "active" : "inactive" })}
                    className="w-full bg-black/50 border border-white/10 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#E50914]"
                  >
                    <option value="">— None —</option>
                    <option value="basic">Basic</option>
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-gray-400 mb-1.5">Status</label>
                  <select
                    data-testid="create-user-status"
                    value={createForm.subscription_status}
                    onChange={(e) => setCreateForm({ ...createForm, subscription_status: e.target.value })}
                    className="w-full bg-black/50 border border-white/10 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#E50914]"
                  >
                    <option value="inactive">Inactive</option>
                    <option value="active">Active</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={closeCreate} className="px-4 py-2 text-sm text-gray-400 hover:text-white" data-testid="create-user-cancel">
                Cancel
              </button>
              <button
                type="submit"
                disabled={createBusy}
                className="btn-red px-5 py-2 rounded text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
                data-testid="create-user-submit"
              >
                {createBusy && <RefreshCcw className="w-4 h-4 animate-spin" />}
                Create user
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" data-testid="confirm-delete-modal">
          <div className="bg-[#141414] border border-red-500/30 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/40 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold">Delete user?</h3>
                <p className="text-sm text-gray-400 mt-1 break-all">{confirmDelete.email}</p>
              </div>
              <button type="button" onClick={() => setConfirmDelete(null)} className="text-gray-400 hover:text-white p-1" data-testid="confirm-delete-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-300 leading-relaxed">
              This is <span className="text-red-300 font-semibold">permanent and cannot be undone</span>.
              The user&apos;s submissions, votes and uploaded files will remain but their author reference will be orphaned.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white"
                data-testid="confirm-delete-cancel"
                disabled={confirmBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={performDelete}
                disabled={confirmBusy}
                className="px-5 py-2 rounded text-sm font-semibold bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 flex items-center gap-2"
                data-testid="confirm-delete-submit"
              >
                {confirmBusy && <RefreshCcw className="w-4 h-4 animate-spin" />}
                Yes, delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
